import React, { useState, useEffect } from 'react'
import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { authApi } from '../lib/api/auth'
import {
  LayoutDashboard,
  Users,
  Calendar,
  FileText,
  Settings,
  LogOut,
  Video,
  ClipboardList,
  BarChart3,
  Shield,
  User,
  Briefcase,
  Star,
  Menu,
  X,
} from 'lucide-react'

interface NavItem {
  icon: React.ElementType
  label: string
  path: string
}

const menuItems: Record<string, NavItem[]> = {
  admin: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
    { icon: Users, label: 'Users', path: '/admin/users' },
    { icon: Calendar, label: 'Sessions', path: '/admin/sessions' },
    { icon: FileText, label: 'Content', path: '/admin/content' },
    { icon: BarChart3, label: 'Reports', path: '/admin/reports' },
    { icon: Shield, label: 'Security', path: '/admin/security' },
    { icon: Briefcase, label: 'Applications', path: '/admin/applications' },
    { icon: Settings, label: 'Settings', path: '/admin/settings' },
  ],
  client: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/client/home' },
    { icon: User, label: 'My Profile', path: '/client/profile' },
    { icon: Calendar, label: 'Schedule a Session', path: '/client/schedule' },
    { icon: Video, label: 'Manage Interviews', path: '/client/interviews' },
    { icon: FileText, label: 'View Feedback', path: '/client/feedback' },
  ],
  interviewer: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/interviewer' },
    { icon: Users, label: 'Clients', path: '/interviewer/clients' },
    { icon: Calendar, label: 'Schedule', path: '/interviewer/schedule' },
    { icon: ClipboardList, label: 'Evaluations', path: '/interviewer/evaluations' },
    { icon: Video, label: 'Recordings', path: '/interviewer/recordings' },
    { icon: Star, label: 'My Ratings', path: '/interviewer/ratings' },
    { icon: BarChart3, label: 'My Reports', path: '/interviewer/reports' },
    { icon: User, label: 'My Profile', path: '/interviewer/settings' },
  ],
}

const roleLabels: Record<string, string> = {
  admin: 'Administrator',
  client: 'Interview Candidate',
  interviewer: 'Interviewer',
}

interface LayoutProps {
  children: React.ReactNode
  role: 'client' | 'interviewer' | 'admin'
}

export default function Layout({ children, role }: LayoutProps) {
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  useEffect(() => {
    setSidebarOpen(false)
  }, [location.pathname])

  const handleLogout = async () => {
    try {
      await authApi.logout()
    } catch {
      // ignore logout API errors
    }
    logout()
    navigate('/signin')
  }

  const items = menuItems[role] || []

  return (
    <div className="layout">
      <button
        className="mobile-menu-btn"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        type="button"
      >
        {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <img
            src="/InterviewDressRehearsal_LOGO.png"
            alt="IDR Logo"
            className="sidebar-logo"
          />
          <div className="sidebar-brand">
            <span className="brand-name">IDR Portal</span>
            <span className="brand-role">{roleLabels[role]}</span>
          </div>
        </div>

        <nav className="sidebar-nav">
          {items.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `nav-item ${isActive ? 'active' : ''}`
              }
            >
              <item.icon size={20} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="avatar">
              {user?.name?.charAt(0) || 'U'}
            </div>
            <div className="user-details">
              <span className="user-name">{user?.name}</span>
              <span className="user-email">{user?.email}</span>
            </div>
          </div>
          <button className="logout-btn" onClick={handleLogout} type="button">
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      <main className="main-content">{children}</main>

      <style>{`
        .layout {
          display: flex;
          min-height: 100vh;
        }

        .sidebar {
          width: 280px;
          background: #ffffff;
          color: var(--sidebar-text);
          display: flex;
          flex-direction: column;
          position: fixed;
          top: 0;
          left: 0;
          bottom: 0;
          z-index: 100;
          border-right: 1px solid var(--sidebar-border, #e5e7eb);
          box-shadow: 1px 0 4px rgba(0, 0, 0, 0.04);
        }

        .sidebar-header {
          padding: 24px;
          display: flex;
          align-items: center;
          gap: 16px;
          border-bottom: 1px solid var(--sidebar-border, #e5e7eb);
        }

        .sidebar-logo {
          width: 48px;
          height: 48px;
          border-radius: 12px;
        }

        .sidebar-brand {
          display: flex;
          flex-direction: column;
        }

        .brand-name {
          font-size: 18px;
          font-weight: 700;
          color: var(--primary-navy);
        }

        .brand-role {
          font-size: 12px;
          color: var(--text-muted);
        }

        .sidebar-nav {
          flex: 1;
          padding: 24px 16px;
          display: flex;
          flex-direction: column;
          gap: 4px;
          overflow-y: auto;
        }

        .nav-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          border-radius: 10px;
          color: var(--text-muted);
          transition: all 0.2s;
          font-size: 14px;
          font-weight: 500;
          text-decoration: none;
        }

        .nav-item:hover {
          background: var(--sidebar-hover, #f3f4f6);
          color: var(--sidebar-text, #374151);
        }

        .nav-item.active {
          background: var(--sidebar-active, rgba(45, 55, 72, 0.08));
          color: var(--sidebar-active-text, #2d3748);
          font-weight: 600;
        }

        .nav-item svg {
          flex-shrink: 0;
        }

        .sidebar-footer {
          padding: 16px;
          border-top: 1px solid var(--sidebar-border, #e5e7eb);
        }

        .user-info {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          background: var(--page-bg);
          border-radius: 10px;
          margin-bottom: 12px;
        }

        .user-details {
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .user-name {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-dark);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .user-email {
          font-size: 12px;
          color: var(--text-muted);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .logout-btn {
          display: flex;
          align-items: center;
          gap: 12px;
          width: 100%;
          padding: 12px 16px;
          background: transparent;
          border: 1px solid var(--border);
          border-radius: 10px;
          color: var(--text-muted);
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .logout-btn:hover {
          background: rgba(229, 62, 62, 0.06);
          border-color: rgba(229, 62, 62, 0.3);
          color: var(--danger);
        }

        .main-content {
          flex: 1;
          margin-left: 280px;
          background: var(--page-bg);
          min-height: 100vh;
          padding: 32px;
        }

        .mobile-menu-btn {
          display: none;
          position: fixed;
          top: 16px;
          left: 16px;
          z-index: 200;
          width: 44px;
          height: 44px;
          border-radius: 10px;
          background: #ffffff;
          color: var(--primary-navy);
          border: 1px solid var(--border);
          cursor: pointer;
          align-items: center;
          justify-content: center;
          box-shadow: var(--shadow);
        }

        .sidebar-overlay {
          display: none;
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.3);
          z-index: 90;
        }

        @media (max-width: 1024px) {
          .sidebar {
            width: 240px;
          }
          .main-content {
            margin-left: 240px;
            padding: 24px;
          }
        }

        @media (max-width: 768px) {
          .mobile-menu-btn {
            display: flex;
          }
          .sidebar-overlay {
            display: block;
          }
          .sidebar {
            transform: translateX(-100%);
            transition: transform 0.3s ease;
          }
          .sidebar.open {
            transform: translateX(0);
          }
          .main-content {
            margin-left: 0;
            padding: 70px 16px 16px 16px;
          }
          .sidebar-header {
            padding: 16px;
          }
          .sidebar-logo {
            width: 40px;
            height: 40px;
          }
          .brand-name {
            font-size: 16px;
          }
        }
      `}</style>
    </div>
  )
}
